import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Copy, Check, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const { refreshUser, isAdmin } = useAuth();
  const [step, setStep] = useState('loading'); // loading | scan | confirm | codes
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    const init = async () => {
      try {
        const { data } = await axios.post(`${API}/auth/totp/setup-init`, {}, { withCredentials: true });
        setQrCode(data.qr_code);
        setSecret(data.secret);
        setStep('scan');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
      }
    };
    init();
  }, [isAdmin, navigate]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error('Enter the 6-digit code from your authenticator');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/totp/setup-confirm`,
        { code: code.trim() },
        { withCredentials: true }
      );
      setRecoveryCodes(data.recovery_codes || []);
      setStep('codes');
      toast.success('2FA enabled!');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 1500);
    } catch {
      toast.error('Could not copy. Long-press to select the secret.');
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [
        'Tune4Tune Admin Recovery Codes\n',
        '==============================\n\n',
        'Store these in a safe place. Each can be used only once if you lose access to your authenticator app.\n\n',
        recoveryCodes.join('\n'),
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tune4tune-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFinish = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl bg-surface border border-[#262626] rounded-2xl p-8 sm:p-10"
        data-testid="2fa-setup-page"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime/10 mb-4">
          <ShieldCheck className="w-7 h-7 text-lime" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-2">Set up two-factor authentication</h1>
        <p className="text-[#A1A1AA] mb-8">
          As an admin you must protect your account with an authenticator app. This takes about 60 seconds.
        </p>

        {step === 'loading' && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-lime" />
          </div>
        )}

        {step === 'scan' && (
          <div className="space-y-6" data-testid="2fa-scan-step">
            <div>
              <h2 className="font-semibold mb-1">1. Scan this QR code</h2>
              <p className="text-sm text-[#A1A1AA] mb-4">
                Use Google Authenticator, 1Password, Authy, or any other TOTP app.
              </p>
              {qrCode && (
                <div className="inline-block p-3 bg-white rounded-lg">
                  <img src={qrCode} alt="2FA QR code" className="w-48 h-48" data-testid="2fa-qr-image" />
                </div>
              )}
            </div>

            <div>
              <h2 className="font-semibold mb-1">Can't scan? Enter this code manually:</h2>
              <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[#262626] rounded-lg p-3 mt-2">
                <code className="flex-1 font-mono text-sm break-all text-lime" data-testid="2fa-secret-text">{secret}</code>
                <Button type="button" variant="ghost" size="icon" onClick={copySecret} data-testid="2fa-copy-secret">
                  {copiedSecret ? <Check className="w-4 h-4 text-lime" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <Button type="button" onClick={() => setStep('confirm')} className="btn-primary w-full" data-testid="2fa-continue">
              I've scanned the code — continue
            </Button>
          </div>
        )}

        {step === 'confirm' && (
          <form onSubmit={handleConfirm} className="space-y-6" data-testid="2fa-confirm-step">
            <h2 className="font-semibold">2. Enter the 6-digit code from your authenticator</h2>
            <div>
              <Label htmlFor="totp-code">Verification code</Label>
              <Input
                id="totp-code"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456"
                className="input-dark w-full text-center text-2xl tracking-[0.4em] font-mono mt-2"
                maxLength={8}
                data-testid="2fa-confirm-code"
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep('scan')} className="border-[#333] flex-1">
                Back
              </Button>
              <Button type="submit" disabled={submitting} className="btn-primary flex-1" data-testid="2fa-confirm-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'Enable 2FA'}
              </Button>
            </div>
          </form>
        )}

        {step === 'codes' && (
          <div className="space-y-6" data-testid="2fa-codes-step">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-100">
                Save these recovery codes <strong>now</strong>. They are the only way to sign in if you lose your authenticator. They will <strong>not</strong> be shown again.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 bg-[#0a0a0a] border border-[#262626] rounded-lg p-4 font-mono text-sm">
              {recoveryCodes.map((c) => (
                <div key={c} className="text-lime" data-testid={`2fa-recovery-${c}`}>{c}</div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={downloadCodes} className="border-[#333] flex-1" data-testid="2fa-download-codes">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
              <Button type="button" onClick={handleFinish} className="btn-primary flex-1" data-testid="2fa-finish">
                I've saved them — go to Admin
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
